import Select from 'react-select'
import { ReactComponent as SearchIcon } from '../../assets/arv/search.svg'
import { fieldError, blockSpecialNumberKeys, blockSpecialNumberPaste, blockSpecialNumberDrop } from './fieldLimits'

export const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  // { value: 'multi_family',  label: 'Multi Family' },
  // { value: 'condo',         label: 'Condo' },
  // { value: 'townhouse',     label: 'Townhouse' },
]

// Geometry from files/sow2/spec/arv.md section 4a, measured out of the 18.08 export by coordinate:
// Property Type 167x39, Beds and Baths 47x39, Sqft and Year Built 71x39, all r 3.5 on #C6CFC6, 17px
// apart, and the button 140x40 fully rounded with its top edge on the fields' rather than on the
// labels'.
const BORDER = '#C6CFC6'
const BORDER_FOCUS = '#4CBC18'
const BORDER_ERROR = '#D85263'

// Row order, used to pick which single field's pill to show when more than one is invalid at once.
const FIELD_ORDER = ['beds', 'baths', 'sqft', 'yearBuiltMin']

const selectStyles = {
  // isFocused read for the same reason as in the filter row: the `border` shorthand below wipes the
  // borderColor react-select derives from its focus state, and boxShadow: 'none' takes the ring with
  // it, leaving a control with a hover style and no focus style.
  control: (base, state) => ({
    ...base,
    width: 167,
    minHeight: 39,
    height: 39,
    border: `1px solid ${state.isFocused ? BORDER_FOCUS : BORDER}`,
    borderRadius: 3.5,
    boxShadow: 'none',
    fontSize: 14,
    cursor: 'pointer',
    backgroundColor: 'white',
    '&:hover': { borderColor: BORDER_FOCUS },
  }),
  // Comment Set #2, item 3.4: field values are weight 500 in the export (`Single Family`, font-size
  // 14 font-weight="500") — the 18.08 export this screen was first built from collapses every
  // weight below 700 to "regular" and missed it.
  singleValue: (base) => ({ ...base, fontSize: 14, fontWeight: 500, color: '#17270D' }),
  valueContainer: (base) => ({ ...base, paddingLeft: 9.5, paddingRight: 0 }),
  // Comment Set #3, item 4, same sibling bug: `isSelected`/`isFocused` painted a background the
  // instant the menu opened, before the cursor ever reached it. Same fix as
  // ComparableFilterRow.jsx's showSelectStyles.option — real `&:hover` only.
  option: (base) => ({
    ...base,
    backgroundColor: '#fff',
    color: '#17270D',
    fontSize: 14,
    cursor: 'pointer',
    ':active': { backgroundColor: '#f8fef4' },
    '&:hover': { backgroundColor: '#f8fef4' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, caretColor: 'transparent' }),
}

const labelCls = 'block text-[13px] text-brand-secondary mb-[5px]'

const fieldCls = `
  shrink-0 h-[39px] rounded-[3.5px] border bg-white
  text-[14px] font-medium text-brand-dark text-center
  focus:outline-none px-[6px]
  placeholder:text-brand-placeholder
`.trim()

// Same rule as the filter row: the border is a class so a focus: variant can win over it, and an
// invalid field keeps its red edge while the caret is in it.
const fieldBorderCls = (error) => error
  ? 'border-[#D85263] focus:border-[#D85263]'
  : 'border-[#C6CFC6] hover:border-[#4CBC18] focus:border-[#4CBC18]'

// The drawn message pill: h 17.5, r 3.75, #FDEDEF on #D85263, 12px, flush with its own field and
// 6.7px below it (frames btn_81, btn_82, btn_83). Out of flow so the row does not move when a value
// goes out of range.
function FieldError({ message }) {
  if (!message) return null
  return (
    <span
      role="alert"
      className="absolute left-0 z-10 whitespace-nowrap rounded-[3.75px] border text-[12px] leading-[17.5px] px-[13.8px]"
      style={{ top: 'calc(100% + 6.7px)', height: 17.5, background: '#FDEDEF', borderColor: BORDER_ERROR, color: BORDER_ERROR }}
    >
      {message}
    </span>
  )
}

// No lastSearchedAt here, and that is the point rather than an oversight. `Last Search: <date>` used
// to draw under this row via ArvMap, from the same prop and the same en-US format — mr-02 added a
// second copy and shipped two identical lines one under the other, caught by looking at the screen,
// invisible to the suite, because no test renders the page and the map together. Comment Set #1, ARV
// 1 moved it again, into the page header above Search Criteria (pages/Arv.jsx), and ArvMap no longer
// draws it at all. The one lesson that survives both moves: this component still has no reason to
// take the prop.
export default function SearchCriteria({
  criteria, onCriteriaChange, onCriteriaBlur, onSearch, searching,
  canSearch = true, hasSearched = false,
}) {
  const set = (field) => (e) => onCriteriaChange({ ...criteria, [field]: e.target.value })

  const bedsError = fieldError('beds', criteria.beds)
  const bathsError = fieldError('baths', criteria.baths)
  const sqftError = fieldError('sqft', criteria.sqftMin)
  // mr-14 fix 2: Year Built travels to the provider on a Search exactly like the three above
  // (buildSearchPayload sends all eight fields, searchArvService writes them verbatim) but was
  // missing from this check — a Search or an Update Analysis could run with an out-of-range value
  // and durably store it in arv.criteria, and from that point hasRecomputeFieldError blocks every
  // future recompute, silently, until someone happens to notice and fix this one field.
  const yearBuiltError = fieldError('yearBuiltMin', criteria.yearBuiltMin)

  // Each pill below is absolutely positioned flush under its own field, independent of its
  // neighbors, so two invalid fields at once land their pills in the same vertical band and
  // overlap. Showing only the first invalid field's pill, in row order, removes the overlap
  // without moving the pill for the (far more common) single-error case, which already matches
  // its drawn position. The red border stays on every invalid field regardless — the button below
  // already blocks on any one of them, so which single message is visible does not change what is
  // blocking the search.
  const fieldErrorsByName = { beds: bedsError, baths: bathsError, sqft: sqftError, yearBuiltMin: yearBuiltError }
  const shownErrorField = FIELD_ORDER.find((field) => fieldErrorsByName[field])

  // A search with an out-of-range criterion is not sent. All four values travel to the provider and
  // shape the ranking, unlike Radius and Time which now filter locally — so letting one through
  // would run a 20-second search whose answer is built on a number the screen is already calling
  // wrong.
  const hasFieldError = Boolean(bedsError || bathsError || sqftError || yearBuiltError)

  // Search before the first one has ever run, Update Analysis after — and disabled whenever the
  // criteria have not moved since the last search. His three sentences, in order: "Search becomes
  // disabled after the initial search and remains disabled until the user updates the Search
  // Criteria", "if the user updates the Search Criteria after the initial search, Search → Update
  // Analysis", and "after it updates, Update Analysis becomes disabled unless the user updates the
  // Search Criteria again".
  //
  // One divergence, deliberate: between a finished search and the user's first edit his text leaves
  // the disabled button reading Search, and this reads Update Analysis. Following him exactly there
  // would put the label back to Search after every re-run, which is what his third sentence rules
  // out. A button whose label changes twice per cycle is also harder to read than one that says
  // what pressing it would do.
  const label = hasSearched ? 'Update Analysis' : 'Search'
  const disabled = searching || hasFieldError || !canSearch

  return (
    <div>
      {/* Comment Set #1, ARV 3: 16/800, details-edit/Frame 310_14 in sow2-section-lean.svg. */}
      <p className="text-[16px] font-extrabold text-brand-dark mb-[10px]">Search Criteria</p>

      {/* One row — property specs and the button. Radius and Time used to sit in a second row here
          and are now in the filter row under the map: "Relocate below the map to Filters section".
          The verb is relocate, so the fields moved and were not rebuilt. */}
      <div className="flex items-end gap-x-[17px]">

        {/* Comment Set #1, ARV 6.4 names "all fields" for the special-character guard below, but
            this one has no free-text surface to guard: `isSearchable={false}` means react-select
            never lets a keystroke change its value, only an option click does, so there is no `6+`
            to type here in the first place. Confirmed against the rendered control, not assumed
            from the prop — focusing it and sending keys changes nothing on screen or in `criteria`.
            The ten inputs this guard actually applies to are the four below and the six in
            ComparableFilterRow.jsx. */}
        <div className="flex flex-col shrink-0" style={{ width: 167 }}>
          <span className={labelCls}>Property Type</span>
          <Select
            value={PROPERTY_TYPES.find(o => o.value === criteria.propertyType) ?? null}
            options={PROPERTY_TYPES}
            onChange={(opt) => {
              const next = { ...criteria, propertyType: opt?.value ?? '' }
              onCriteriaChange(next)
              onCriteriaBlur?.(next)
            }}
            styles={selectStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
            isClearable={false}
          />
        </div>

        <div className="flex flex-col relative shrink-0">
          <span className={labelCls}>Beds</span>
          <input
            type="number" min="0" max="15"
            aria-label="Beds"
            value={criteria.beds ?? ''}
            onChange={set('beds')}
            onKeyDown={blockSpecialNumberKeys}
            onPaste={blockSpecialNumberPaste}
            onDrop={blockSpecialNumberDrop}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 47 }}
            className={`${fieldCls} ${fieldBorderCls(bedsError)}`}
          />
          <FieldError message={shownErrorField === 'beds' ? bedsError : null} />
        </div>

        <div className="flex flex-col relative shrink-0">
          <span className={labelCls}>Baths</span>
          <input
            type="number" min="0" max="15"
            aria-label="Baths"
            value={criteria.baths ?? ''}
            onChange={set('baths')}
            onKeyDown={blockSpecialNumberKeys}
            onPaste={blockSpecialNumberPaste}
            onDrop={blockSpecialNumberDrop}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 47 }}
            className={`${fieldCls} ${fieldBorderCls(bathsError)}`}
          />
          <FieldError message={shownErrorField === 'baths' ? bathsError : null} />
        </div>

        {/* Comment Set #1, ARV 6.1 — Sqft and Year Built say Any, not Min. Both are single values,
            and Beds and Baths beside them already said Any, so Min was the odd pair out here.

            The Sqft PAIR in the filter row keeps Min and Max, and that is deliberate: the export
            draws them, `filters-table/.../input_179` as 'Min' and input_180 as 'Max' in the
            placeholder colour #8A9A85, beside a Price pair drawn the same way. Year Built exists
            only on this panel, so his two names point here; had he meant the filter row he would
            have had to name Price too, since the two pairs there are identical. */}
        <div className="flex flex-col relative shrink-0">
          <span className={labelCls}>Sqft</span>
          <input
            type="number" min="0" max="10000"
            aria-label="Sqft"
            value={criteria.sqftMin ?? ''}
            onChange={set('sqftMin')}
            onKeyDown={blockSpecialNumberKeys}
            onPaste={blockSpecialNumberPaste}
            onDrop={blockSpecialNumberDrop}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 71 }}
            className={`${fieldCls} ${fieldBorderCls(sqftError)}`}
          />
          <FieldError message={shownErrorField === 'sqft' ? sqftError : null} />
        </div>

        <div className="flex flex-col relative shrink-0">
          <span className={labelCls}>Year Built</span>
          <input
            type="number" min="1800" max="2099"
            aria-label="Year Built"
            value={criteria.yearBuiltMin ?? ''}
            onChange={set('yearBuiltMin')}
            onKeyDown={blockSpecialNumberKeys}
            onPaste={blockSpecialNumberPaste}
            onDrop={blockSpecialNumberDrop}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 71 }}
            className={`${fieldCls} ${fieldBorderCls(yearBuiltError)}`}
          />
          <FieldError message={shownErrorField === 'yearBuiltMin' ? yearBuiltError : null} />
        </div>

        {/* Button — top edge on the fields', not on the labels'. Disabled fill #A9B9A3 is from the
            09.08 export, where both buttons are drawn in both states; the 18.08 render carries only
            the enabled Search (files/sow2/spec/arv.md section 2). */}
        <button
          onClick={onSearch}
          disabled={disabled}
          style={{ width: hasSearched ? 173 : 140, height: 40, background: disabled ? '#A9B9A3' : '#4CBC18' }}
          className="shrink-0 rounded-full text-white text-[14px] flex items-center justify-center gap-x-[6px] transition-opacity enabled:hover:opacity-90"
        >
          <SearchIcon />
          {searching ? 'Analyzing…' : label}
        </button>

      </div>
    </div>
  )
}
